import { client } from '../apiClient';
import { AccountResponse } from '../../models/account/AccountResponse';
import { ProjectResponse, ProjectResponseData } from '../../models/projects/ProjectResponse';
import { MonitorPrompt } from '../../models/monitor/MonitorPrompt';
import { ProjectNameData } from '../../models/projects/ProjectNameData';
import { QuestionResponse } from '../../models/projects/QuestionResponse';
import { ProjectData } from '../../../pages/projects/wizard/models/ProjectData';
import { ImportProjectData } from '../../models/projects/ImportProjectData';
import { SearchVolumeResponse } from '../../models/projects/SearchVolumeResponse';
import { ChatType } from '../../types/monitor/ChatType';
import { getDefaultRange } from '../../utils/utils';
import { format } from 'date-fns';

export const PROJECTS_ENDPOINT = '/projects';

export const changeProjectName = async (changeProjectNameData: ProjectNameData): Promise<boolean> => {
  return client(true, true)
    .post<MonitorPrompt[]>(`${PROJECTS_ENDPOINT}/change-name`, changeProjectNameData)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getProjects = async (force = false): Promise<ProjectResponse[]> => {
  const query = force ? '?force=true' : '';
  return client(true, true)
    .get<AccountResponse[]>(`${PROJECTS_ENDPOINT}/${query}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getProject = async (projectId: number): Promise<ProjectResponse> => {
  const { data } = await client(true, true).get<ProjectResponse>(`${PROJECTS_ENDPOINT}/${projectId}`);
  return data;
};

export const importProjects = async (data: ImportProjectData[]): Promise<boolean> => {
  return client(true, true)
    .post<boolean>(`${PROJECTS_ENDPOINT}/import`, data)
    .then((response) => response.data)
    .catch((error) => error);
};

export const removeProject = async (id: number): Promise<boolean> => {
  return client(true, true)
    .delete(`${PROJECTS_ENDPOINT}/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const removeProjectsBulk = async (ids: number[]): Promise<boolean> => {
  return client(true, true)
    .delete(`${PROJECTS_ENDPOINT}/bulk`, { data: { ids } })
    .then((response) => response.data)
    .catch((error) => error);
};

export const duplicateProject = async (id: number): Promise<boolean> => {
  return client(true, true)
    .post(`${PROJECTS_ENDPOINT}/copy/${id}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const changeProjectOwner = async (projectId: number, newOwnerId: number): Promise<boolean> => {
  return client(true, true)
    .post(`${PROJECTS_ENDPOINT}/change-owner/${projectId}/${newOwnerId}`)
    .then((response) => response.data)
    .catch((error) => error);
};

export const setProjectDemo = async (
  projectId: number,
  demoVersion: string | null
): Promise<boolean> => {
  const data = {
    projectId,
    demoVersion,
  };
  return client(true, true)
    .post(`${PROJECTS_ENDPOINT}/set-demo`, data)
    .then((response) => response.data)
    .catch((error) => error);
};

export const getQuestions = async (text: string, isDomain: boolean): Promise<QuestionResponse> => {
  const data = {
    text,
    isDomain,
  };
  return client(true, true)
    .post<QuestionResponse>(`${PROJECTS_ENDPOINT}/question`, data)
    .then((response) => response.data);
};

export const createProject = async (projectData: ProjectData): Promise<ProjectResponseData> => {
  const { data } = await client(true, true).post<ProjectResponseData>(
    `${PROJECTS_ENDPOINT}/add`,
    projectData
  );
  return data;
};

export const getSearchVolume = async (
  ids: number[],
  questions: string[],
  firmName: string | null,
  projectCountry: string | null
): Promise<SearchVolumeResponse[]> => {
  const { data } = await client(true, true).post<SearchVolumeResponse[]>(
    `${PROJECTS_ENDPOINT}/search-volume`,
    {
      ids,
      questions,
      firmName,
      projectCountry,
    }
  );
  return data;
};

export const updateDomainClassification = async (
  projectId: number,
  domainClassification: Record<string, string>
): Promise<void> => {
  return client(true, true)
    .patch(`${PROJECTS_ENDPOINT}/${projectId}/domain-classification`, { domainClassification })
    .then((response) => response.data)
    .catch((error) => error);
};

export interface RegenerateSuggestedQuestionsResponse {
  brand: string;
  questions: string[];
  deletedCount: number;
}

export const regenerateSuggestedQuestions = async (
  projectId: number,
  language: string | null
): Promise<RegenerateSuggestedQuestionsResponse> => {
  const { data } = await client(true, true).post<RegenerateSuggestedQuestionsResponse>(
    `${PROJECTS_ENDPOINT}/${projectId}/regenerate-suggested-questions`,
    { language }
  );
  return data;
};

export const exportProjectSources = async (
  projectId: number,
  chatType: ChatType,
  domain: string,
  from?: Date | null,
  to?: Date | null
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  return client(true, true)
    .get<Blob>(
      `${PROJECTS_ENDPOINT}/export-project-sources/${projectId}/${formattedFrom}/${formattedTo}/${chatType}/${domain}`,
      {
        responseType: 'blob',
      }
    )
    .then((response) => response.data)
    .catch((error) => error);
};
